// src/pages/RestaurantPortalPage.tsx
import { useEffect, useMemo, useState } from 'react';
import { supabase, type Restaurant, type MenuItem } from '../lib/supabase';
import { RefreshCw, LogOut, CheckCircle2, XCircle, Eye, EyeOff } from 'lucide-react';

type Order = {
  id: string;
  customer_name: string;
  customer_address: string;
  total: number;
  status: 'pending' | 'assigned' | 'delivering' | 'completed' | 'cancelled';
  created_at: string;
};

type OrderItemRow = {
  id: string;
  order_id: string;
  quantity: number;
  price: number;
  size?: string | null;
  menu_items?: { name: string } | null;
};

export default function RestaurantPortalPage() {
  const [ridInput, setRidInput] = useState('');
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [loading, setLoading] = useState(false);

  const [menu, setMenu] = useState<MenuItem[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [itemsByOrder, setItemsByOrder] = useState<Record<string, OrderItemRow[]>>({});
  const [poll, setPoll] = useState(true);

  // Auto-refresh hvert 20. sekund når innlogget
  useEffect(() => {
    if (!restaurant || !poll) return;
    const id = setInterval(() => { void refreshData(); }, 20000);
    return () => clearInterval(id);
  }, [restaurant, poll]);

  async function signIn() {
    const rid = ridInput.trim();
    if (!rid) {
      alert('Skriv inn restaurant-ID.');
      return;
    }
    setLoading(true);
    const { data, error } = await supabase.from('restaurants').select('*').eq('id', rid).maybeSingle();
    setLoading(false);
    if (error || !data) {
      alert('Fant ikke restaurant på den ID-en.');
      return;
    }
    setRestaurant(data as Restaurant);
    await refreshData(rid);
  }

  async function refreshData(rid?: string) {
    const restaurantId = rid ?? restaurant?.id;
    if (!restaurantId) return;
    setLoading(true);

    // Hent meny
    const { data: dMenu } = await supabase
      .from('menu_items')
      .select('id, name, description, price, category, is_available')
      .eq('restaurant_id', restaurantId)
      .order('category', { ascending: true })
      .order('name', { ascending: true });

    setMenu(dMenu || []);

    // Hent siste 100 ordre for denne restauranten
    const { data: dOrders } = await supabase
      .from('orders')
      .select('id, customer_name, customer_address, total, status, created_at')
      .eq('restaurant_id', restaurantId)
      .order('created_at', { ascending: false })
      .limit(100);

    setOrders(dOrders || []);

    // Hent ordrelinjer
    const ids = (dOrders || []).map(o => o.id);
    if (ids.length) {
      const { data: dItems } = await supabase
        .from('order_items')
        .select('id, order_id, quantity, price, size, menu_items(name)')
        .in('order_id', ids);

      const grouped: Record<string, OrderItemRow[]> = {};
      (dItems || []).forEach(oi => {
        (grouped[oi.order_id] ||= []).push(oi);
      });
      setItemsByOrder(grouped);
    } else {
      setItemsByOrder({});
    }

    setLoading(false);
  }

  async function toggleActive() {
    if (!restaurant) return;
    const next = !restaurant.is_active;
    const { error } = await supabase
      .from('restaurants')
      .update({ is_active: next })
      .eq('id', restaurant.id);
    if (error) {
      alert('Klarte ikke oppdatere status.');
      return;
    }
    setRestaurant({ ...restaurant, is_active: next });
  }

  async function toggleItem(itemId: string, current: boolean) {
    const { error } = await supabase
      .from('menu_items')
      .update({ is_available: !current })
      .eq('id', itemId);
    if (error) {
      alert('Klarte ikke oppdatere vare.');
      return;
    }
    setMenu(prev => prev.map(m => (m.id === itemId ? { ...m, is_available: !current } : m)));
  }

  const groupedMenu = useMemo(() => {
    return menu.reduce((acc, m) => {
      const cat = m.category || 'Annet';
      (acc[cat] ||= []).push(m);
      return acc;
    }, {} as Record<string, MenuItem[]>);
  }, [menu]);

  if (!restaurant) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-white dark:bg-gray-800 rounded-xl p-6 shadow">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Restaurantportal</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Logg inn med din <span className="font-semibold">restaurant-ID</span>.
          </p>
          <input
            value={ridInput}
            onChange={e => setRidInput(e.target.value)}
            placeholder="f.eks. 0e107de2-b0c1-4e44-aacd-65ead712e73b"
            className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white mb-3"
          />
          <button
            onClick={signIn}
            disabled={loading}
            className="w-full py-3 rounded-lg bg-[#004D99] hover:bg-[#003366] text-white font-semibold"
          >
            {loading ? 'Sjekker…' : 'Gå videre'}
          </button>
          <p className="text-xs text-gray-500 mt-3">Pust med magen. Ja, du må bruke riktig ID.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-6xl mx-auto px-4 space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {restaurant.logo_url ? (
              <img
                src={restaurant.logo_url}
                alt={restaurant.name}
                className="w-12 h-12 rounded object-contain bg-white"
              />
            ) : (
              <div className="w-12 h-12 rounded bg-gray-200 dark:bg-gray-700" />
            )}
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {restaurant.name}
              </h1>
              <div className="text-sm">
                {restaurant.is_active ? (
                  <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-300">
                    <CheckCircle2 className="w-4 h-4" /> Aktiv
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-gray-600 dark:text-gray-300">
                    <XCircle className="w-4 h-4" /> Inaktiv
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={toggleActive}
              className={`px-3 py-2 rounded-lg text-white ${restaurant.is_active ? 'bg-rose-600 hover:bg-rose-700' : 'bg-emerald-600 hover:bg-emerald-700'}`}
            >
              {restaurant.is_active ? 'Sett Inaktiv' : 'Sett Aktiv'}
            </button>
            <button
              onClick={() => void refreshData()}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-900 text-white dark:bg-white dark:text-gray-900"
            >
              <RefreshCw className="w-4 h-4" />
              Oppdater
            </button>
            <button
              onClick={() => setRestaurant(null)}
              className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-gray-800 dark:text-gray-100"
              title="Logg ut"
            >
              <LogOut className="w-4 h-4" />
            </button>
            <button
              onClick={() => setPoll(p => !p)}
              className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-gray-800 dark:text-gray-100"
              title="Slå av/på auto-oppdatering"
            >
              Auto: {poll ? 'På' : 'Av'}
            </button>
          </div>
        </div>

        {/* Siste ordre */}
        <section className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Siste ordrer</h2>
            <span className="text-sm px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
              {orders.length} stk
            </span>
          </div>

          {orders.length === 0 ? (
            <div className="text-gray-600 dark:text-gray-300">Ingen ordrer ennå.</div>
          ) : (
            <div className="space-y-3">
              {orders.map(o => {
                const lines = itemsByOrder[o.id] || [];
                return (
                  <div key={o.id} className="flex flex-col gap-2 rounded border border-gray-200 dark:border-gray-700 p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {o.customer_name} • {o.customer_address}
                        </div>
                        <div className="text-xs text-gray-500">
                          {new Date(o.created_at).toLocaleString()} • {o.status}
                        </div>
                      </div>
                      <div className="text-right text-gray-900 dark:text-white font-semibold">
                        {o.total.toFixed(2)} kr
                      </div>
                    </div>
                    <div className="rounded bg-gray-50 dark:bg-gray-900 p-2 text-sm">
                      {lines.length ? (
                        <ul className="list-disc ml-5 space-y-0.5">
                          {lines.map((it, idx) => (
                            <li key={it.id}>
                              {idx + 1}. {it.quantity > 1 ? `${it.quantity}x ` : ''}
                              {it.menu_items?.name || 'Ukjent vare'}
                              {it.size ? ` (${String(it.size).toLowerCase()})` : ''} – {(it.price * it.quantity).toFixed(2)} kr
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <div className="text-gray-500">Ingen varelinjer.</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Meny og tilgjengelighet */}
        <section className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Meny</h2>

          {Object.entries(groupedMenu).map(([cat, items]) => (
            <div key={cat} className="mb-6">
              <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-300 mb-2">{cat}</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {items.map(item => (
                  <div key={item.id} className="rounded border border-gray-200 dark:border-gray-700 p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">{item.name}</div>
                        {item.description && (
                          <div className="text-xs text-gray-500 line-clamp-2">{item.description}</div>
                        )}
                        <div className="text-sm text-gray-700 dark:text-gray-200 mt-1">
                          {item.price?.toFixed ? item.price.toFixed(2) : item.price} kr
                        </div>
                      </div>
                      <button
                        onClick={() => toggleItem(item.id, item.is_available)}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${
                          item.is_available
                            ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                            : 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-100'
                        }`}
                        title={item.is_available ? 'Skjul fra meny' : 'Gjør tilgjengelig'}
                      >
                        {item.is_available ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        {item.is_available ? 'Skjul' : 'Vis'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              {!items.length && <div className="text-gray-500 text-sm">Ingen varer i denne kategorien.</div>}
            </div>
          ))}

          {!menu.length && <div className="text-gray-500">Menyen er tom. Kanskje på tide å laste opp noe annet enn luft?</div>}
        </section>
      </div>
    </div>
  );
}
